'use client';

import { PortableText, PortableTextBlock, PortableTextComponents } from 'next-sanity';
import Link from 'next/link';
import TwitterEmbed from '../editorials/CustomPortableText/TwitterEmbed';
import InstagramEmbed from '../editorials/CustomPortableText/InstagramEmbed';
import EvervizEmbed from '../editorials/CustomPortableText/EvervizEmbed';
import { YouTubeEmbed } from '@next/third-parties/google';

import styles from '@/styles/components/CustomPortableTextComponents.module.scss';

const customPortableTextComponents: PortableTextComponents = {
  marks: {
    highlight: ({ children }) => (
      <span className={styles.highlight}>{children}</span>
    ),
    link: ({ value, children }) => {
      if (!value?.href) return null
      const target = (value?.href || '').startsWith('http') ? '_blank' : undefined
      return <Link
        href={value?.href}
        target={target}
        rel={target === '_blank' ? 'noindex nofollow' : undefined}
        prefetch={false}
        className={styles.link}>
        {children}
      </Link>
    },
    strong: ({ children }) => <strong className={styles.strong}>{children}</strong>,
    em: ({ children }) => <em>{children}</em>
  },
  block: {
    h1: ({ children }) => <h1>{children}</h1>,
    h2: ({ children }) => <h2>{children}</h2>,
    h3: ({ children }) => <h3>{children}</h3>,
    strong: ({ children }) => <strong className={styles.strong}>{children}</strong>,
    normal: ({ children }) => <p className={styles.paragraph}>{children}</p>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>
  },
  list: {
    bullet: ({ children }) => <ul>{children}</ul>,
    number: ({ children }) => <ol>{children}</ol>
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>
  },
  types: {
    figure: ({ value, isInline }) => {
      const { mobile, tablet, desktop } = value?.sources;
      return (
        <figure className={styles.figure}>
          <picture>
            <source media="(min-width: 1024px)" srcSet={desktop.url} />
            <source media="(min-width: 768px)" srcSet={tablet.url} />
            <img
              src={mobile.url}
              alt={value?.caption}
              style={{ width: '100%', height: 'auto' }}
            />
          </picture>
          {value?.caption && <figcaption className="caption">{value?.caption}</figcaption>}
        </figure>
      );
    },
    twitter: ({ value }) => {
      return <TwitterEmbed className={styles.twitterBlock} data={value.embed} />
    },
    instagram: ({ value }) => {
      return <InstagramEmbed className={styles.twitterBlock} url={value.embed} />
    },
    facebook: ({ value }) => {
      return null;
    },
    youtube: ({ value }) => {
      if (!value?.youtubeId) return null
      return <YouTubeEmbed videoid={value.youtubeId} params="controls=1&rel=0" style="margin: 1.5rem 0" />
    },
    inlinevideo: ({ value }) => {
      const { video } = value;

      if (video.youtubeId) {
        return <YouTubeEmbed videoid={value.video.youtubeId} params="controls=1&rel=0" style="margin: 1.5rem 0" />
      }

      if (video.outlet) {
        const baseUrl = `${process.env.OTT_PROVIDER}/video/`;
        const src = `${baseUrl}${video.id}`;
        return (
          <iframe
            src={src}
            sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin allow-top-navigation allow-presentation"
            allow="encrypted-media"
            allowFullScreen
            style={{
              border: 0,
              width: '100%',
              height: 'auto',
              aspectRatio: '16/9',
              margin: '1.5rem 0',
            }}
          />
        );
      }
      return null
    },
    everviz: ({ value }) => {
      return <EvervizEmbed
        url={value.url}
        className={styles.evervizEmbed}
      />
    }
    /*inlinecontent: ({ value }) => {
      const items = value.stories.map((item: any) => item._ref);
      return JSON.stringify(value)
    },
    inlinelive: ({ value }) => {
      return JSON.stringify(value)
    },
    inlineshare: ({ value }) => {
      return JSON.stringify(value)
    },
    inlinesubscribe: ({ value }) => {
      return JSON.stringify(value)
    },
    inlinerelated: ({ value }) => {
      return JSON.stringify(value)
    },
    imgcompare: ({ value }) => {
      return JSON.stringify(value)
    },
    imagegallery: ({ value }) => {
      return JSON.stringify(value)
    },*/
  }
};

const CustomPortableText = ({ value }: { value: PortableTextBlock[] }) => {
  return (
    <PortableText
      value={value}
      components={customPortableTextComponents}
    />
  );
};

export default CustomPortableText;